<template>
  <div>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card elevation="3" rounded="xl">
            <!-- Page Header -->
            <v-card-title class="pb-3 pt-5 px-6">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center mb-2">
                    <v-btn
                      small
                      color="grey"
                      outlined
                      rounded
                      @click="$router.back()"
                      class="mr-3"
                    >
                      <v-icon left small>mdi-arrow-left</v-icon>
                      Back
                    </v-btn>
                  </div>
                  <h2 class="text-h5 font-weight-bold primary--text mb-1">
                    {{ isDeletePage() != true ? 'Product Investments' : 'Deleted Product Investments' }}
                  </h2>
                  <p class="text-body-2 grey--text ma-0">
                    {{ isDeletePage() != true ? 'Manage and monitor product investment records' : 'Restore deleted investment records' }}
                  </p>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end align-center">
                    <template v-if="isDeletePage() != true">
                      <v-btn
                        color="success"
                        elevation="2"
                        rounded
                        @click="exportToExcel"
                        class="px-4 mr-3"
                      >
                        <v-icon left small>mdi-microsoft-excel</v-icon>
                        Export Investments
                      </v-btn>
                      <v-btn
                        color="primary"
                        elevation="2"
                        rounded
                        to="/investments/create"
                        class="px-5"
                      >
                        <v-icon left small>mdi-plus</v-icon>
                        Add Investment
                      </v-btn>
                    </template>
                    <v-btn
                      v-else
                      color="info"
                      elevation="2"
                      rounded
                      @click="$router.back()"
                      class="px-4"
                    >
                      <v-icon left small>mdi-arrow-left</v-icon>
                      Back to Investments
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider class="mx-6"></v-divider>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-2 pt-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-3" color="primary">mdi-filter-variant</v-icon>
                    <span class="text-subtitle-1 font-weight-medium">Filter Investments</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      small
                      rounded="pill"
                      class="mr-3"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active Filter(s)
                    </v-chip>
                    <v-btn
                      text
                      small
                      color="error"
                      rounded
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left small>mdi-filter-remove</v-icon>
                      Clear All
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-6 pb-6">
              <v-row class="align-center">
                <!-- Search Field -->
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Investments"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    rounded
                    dense
                    clearable
                    placeholder="User name, product, amount..."
                    @click:clear="(filter.search_keyword = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" sm="6" md="2">
                  <v-select
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined
                    rounded
                    dense
                    clearable
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>

                <!-- Investment Amount Range Filter -->
                <v-col cols="12" sm="6" md="3">
                  <v-select
                    v-model="filter.amount_range"
                    :items="amountRanges"
                    label="Amount Range"
                    prepend-inner-icon="mdi-currency-inr"
                    outlined
                    rounded
                    dense
                    clearable
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>

                <!-- Spacer -->
                <v-col cols="12" md="3"></v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Investments Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" rounded="lg">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="24">mdi-account-cash</v-icon>
                    <span class="text-h6 font-weight-medium">Investments List</span>
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined class="mr-3">
                      <v-icon left small>mdi-cash-multiple</v-icon>
                      {{ count }} {{ count === 1 ? "Investment" : "Investments" }} Found
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      Showing {{ (page - 1) * rowsPerPage + 1 }} -
                      {{ Math.min(page * rowsPerPage, count) }} of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-3">
                      <span class="text-caption grey--text mr-2">Show:</span>
                      <v-btn-toggle
                        v-model="selectedRowsPerPage"
                        dense
                        class="mr-2"
                        @change="changeRowsPerPageToggle"
                      >
                        <v-btn small outlined :value="15">15</v-btn>
                        <v-btn small outlined :value="25">25</v-btn>
                        <v-btn small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="investments"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="rowsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * rowsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- Status Column -->
                <template #[`item.is_active`]="{ item }">
                  <v-chip
                    :color="item.is_active ? 'success' : 'error'"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{ item.is_active ? "mdi-check-circle" : "mdi-close-circle" }}
                    </v-icon>
                    {{ item.is_active ? "Active" : "Inactive" }}
                  </v-chip>
                </template>

                <!-- User Column -->
                <template #[`item.user_info`]="{ item }">
                  <div class="font-weight-medium">
                    {{ item.user ? item.user.first_name : "N/A" }}
                  </div>
                </template>

                <!-- Product Column -->
                <template #[`item.product_info`]="{ item }">
                  <div class="font-weight-medium">
                    {{ item.product ? item.product.product_name : "N/A" }}
                  </div>
                </template>

                <!-- Invested Amount Column -->
                <template #[`item.invested_amount`]="{ item }">
                  <div class="font-weight-medium text-success">
                    {{ formatCurrency(item.invested_amount) }}
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3">mdi-account-cash-outline</v-icon>
                    <h3 class="text-h6 grey--text mt-4 mb-2">No Investments Found</h3>
                    <p class="grey--text mb-4">
                      {{ 
                        hasActiveFilters
                          ? "No investments match your current filters. Try adjusting your search criteria."
                          : "No investments have been made for this product yet."
                      }}
                    </p>
                    <v-btn
                      v-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-body-2 grey--text font-weight-medium">
                      Showing {{ (page - 1) * rowsPerPage + 1 }} to
                      {{ Math.min(page * rowsPerPage, count) }} of
                      {{ count }} investments
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Previous Button -->
                    <v-btn
                      small
                      outlined
                      rounded
                      :disabled="page <= 1"
                      @click="page--"
                      class="mr-3"
                    >
                      <v-icon small left>mdi-chevron-left</v-icon>
                      Previous
                    </v-btn>

                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      circle
                      class="mx-2"
                    ></v-pagination>

                    <!-- Next Button -->
                    <v-btn
                      small
                      outlined
                      rounded
                      :disabled="page >= paginationLength"
                      @click="page++"
                      class="ml-3"
                    >
                      Next
                      <v-icon small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-body-2 grey--text mr-3 font-weight-medium">Go to page:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details="auto"
                      rounded
                      hide-spin-buttons
                      class="no-spin-arrows"
                      style="max-width: 90px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <!-- Clear Success Dialog Box -->
      <v-dialog v-model="is_deleted_investment" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-empty
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>Investment deleted successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_deleted_investment = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
      <!-- Restore Success Dialog Box -->
      <v-dialog v-model="is_restored_investment" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>Investment restored successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_restored_investment = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
      <!-- Delete Confirmation Value Dialog -->
      <DeleteConfirmationDialog
        v-if="selectedId !== null"
        :itemId="selectedId"
        @delete="deleteData"
        ref="deleteDialog"
      />
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";
export default {
  components: { DeleteConfirmationDialog },
  name: "investmentsPage",
  data() {
    return {
filter: {
        is_active: "",
        search_keyword: "",
        amount_range: "",
        show_deleted: false,
      },
      investments: [],
statuses: [
        {
          text: "All Status",
          value: "",
        },
        { text: "Active", value: "1" },
        { text: "Inactive", value: "0" },
      ],
      amountRanges: [
        {
          text: "All Amounts",
          value: "",
        },
        { text: "Under ₹1 Lakh", value: "0-100000" },
        { text: "₹1L - ₹5L", value: "100000-500000" },
        { text: "₹5L - ₹10L", value: "500000-1000000" },
        { text: "Above ₹10L", value: "1000000+" },
      ],
      page: 1,
rowsPerPage: 15,
      selectedRowsPerPage: 15,
      jumpToPage: 1,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      delete_investment: false,
      is_deleted_investment: false,
      is_restored_investment: false,
      selectedId: null,
      headers: [
        { text: "Sr No.", value: "sr_no", sortable: false, width: "70px" },
        { text: "Status", value: "is_active", width: "120px" },
        {
          text: "User",
          value: "user_info",
          sortable: false,
          width: "200px",
        },
        {
          text: "Product",
          value: "product_info",
          sortable: false,
          width: "250px",
        },
        {
          text: "Invested Amount",
          value: "invested_amount",
          sortable: false,
          width: "180px",
        },
      ],
    };
  },
  watch: {
    page: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      // Set filters with default values
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.filter.search_keyword = this.filter.search_keyword || "";
      this.filter.is_active = this.filter.is_active || "";
      this.filter.amount_range = this.filter.amount_range || "";

      let query = new URLSearchParams({
        page: this.page,
        rowsPerPage: this.rowsPerPage,
        product_id: this.decryptIt(this.$route.params.productId),
        ...this.filter,
      });

      await axios
        .get(`investments?${query}`)
        .then((response) => {
          this.investments = response.data.data;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
      this.isLoading = false;
    },
    async deleteData(id) {
      this.delete_investment = true;
      await axios
        .post(`/investments/delete/${this.encryptIt(id)}`)
        .then(() => {
          this.delete_investment = false;
          this.is_deleted_investment = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
    },
    async restoreData(id) {
      this.restore_investment = true;
      await axios
        .post(`/investments/restore/${this.encryptIt(id)}`)
        .then(() => {
          this.restore_investment = false;
          this.is_restored_investment = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
    },
    openDeleteDialog(id) {
      this.selectedId = id;
      this.$nextTick(() => {
        this.$refs.deleteDialog.openDialog();
      });
    },

    formatCurrency(amount) {
      if (!amount) return "₹0";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
      }).format(parseFloat(amount) || 0);
    },

clearAllFilters() {
      this.filter = {
        is_active: "",
        search_keyword: "",
        amount_range: "",
        show_deleted: this.filter.show_deleted,
      };
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.is_active) count++;
      if (this.filter.amount_range) count++;
      return count;
    },

    changeRowsPerPageToggle(value) {
      if (value !== null && value !== undefined) {
        this.rowsPerPage = value;
        this.selectedRowsPerPage = value;
        this.page = 1;
        this.getData();
      }
    },

    exportToExcel() {
      console.log("Export to Excel clicked");
      // Implement Excel export logic
    },

    isDeletePage() {
      return this.$route.query.open_trash;
    },

    goToPage() {
      const pageNum = parseInt(this.jumpToPage);
      if (pageNum >= 1 && pageNum <= this.paginationLength) {
        this.page = pageNum;
      } else {
        this.jumpToPage = this.page;
      }
    },
  },
computed: {
    hasActiveFilters() {
      return this.filter.search_keyword || this.filter.is_active || this.filter.amount_range;
    },
  },
};
</script>
